import { Injectable } from '@angular/core';
import { getDoc, doc, setDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { User } from 'src/app/models/user';

import { DataStoreService } from 'src/app/services/data-store/data-store.service';
import { FirebaseService } from '../firebase/firebase.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(
    private firebase: FirebaseService,
    private dataStore: DataStoreService,
  ) {}

  async getCurrentUserDocument(forceFetch = false): Promise<User> {
    const inMemoryUser = this.dataStore.getProperty('currentUser');
    if (!forceFetch && inMemoryUser && inMemoryUser.userId) {
      return inMemoryUser;
    }

    await this.firebase.auth.authStateReady();
    const authUser = this.firebase.auth.currentUser;
    if (!authUser) {
      throw new Error('User not found');
    }

    const userDoc = await getDoc(doc(this.firebase.firestore, `users/${authUser.uid}`));
    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }
    
    const currentUser = userDoc.data() as User;
    if (!currentUser.userId) {
      currentUser.userId = authUser.uid;
    }

    // Ensure organizationIds is an array
    if (!currentUser.organizationIds) {
      currentUser.organizationIds = [];
    }

    this.dataStore.updateState({ currentUser });

    return currentUser;
  }

  async getCurrentAccountId(): Promise<string> {
    const currentUser = this.firebase.auth.currentUser;
    if (!currentUser) {
      throw new Error('UserService: User not logged in');
    }

    const userDoc = await getDoc(doc(this.firebase.firestore, 'users', currentUser.uid));
    const userData = userDoc.data();
    
    if (!userData?.['currentAccountId']) {
      throw new Error('UserService: Current account ID not found');
    }

    return userData['currentAccountId'];
  }

  async getDefaultAccountId(): Promise<string> {
    const currentUser = this.firebase.auth.currentUser;
    if (!currentUser) {
      throw new Error('UserService: User not logged in');
    }

    const userDoc = await getDoc(doc(this.firebase.firestore, 'users', currentUser.uid));
    const userData = userDoc.data();
    
    if (!userData?.['defaultAccountId']) {
      throw new Error('UserService: Default account ID not found');
    }

    return userData['defaultAccountId'];
  }

  async updateDisplayName(displayName: string): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) {
      throw new Error('User not found');
    }

    await updateProfile(user, { displayName });
  }

  async updateThemePreference(theme: 'light' | 'dark'): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) {
      throw new Error('User not found');
    }

    await setDoc(doc(this.firebase.firestore, `users/${user.uid}`), { theme }, { merge: true });

    const currentUser = await this.getCurrentUserDocument();
    currentUser.theme = theme;
  }

  async getThemePreference(): Promise<'light' | 'dark' | undefined> {
    const currentUser = await this.getCurrentUserDocument();
    return currentUser?.theme;
  }

  async getLanguagePreference(): Promise<string | undefined> {
    const currentUser = await this.getCurrentUserDocument();
    return currentUser?.language;
  }

  async updateLanguagePreference(language: string): Promise<void> {
    const user = this.firebase.auth.currentUser;
    if (!user) {
      throw new Error('User not found');
    }

    await setDoc(doc(this.firebase.firestore, `users/${user.uid}`), { language }, { merge: true });

    const currentUser = await this.getCurrentUserDocument();
    currentUser.language = language;
  }
}